import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { Check, CircleHelp, MapPin, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'

import {
    CAN_DO,
    NOT_YET,
    PLANNER_REQUESTS
} from '@/constants/landing'

const Intelligence: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const request = PLANNER_REQUESTS[active]

    return (
        <section id='intelligence' aria-labelledby='intelligence-title' className='bg-background'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-28'>
                <div className='grid gap-6 lg:grid-cols-12 lg:gap-10'>
                    <Reveal className='lg:col-span-7'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember-deep'>
                            Intelligence
                        </p>

                        <h2 id='intelligence-title' className='mt-5 font-heading text-[clamp(2.6rem,5.4vw,5rem)] leading-[0.96] tracking-[-0.02em]'>
                            Knows your home. <em className='italic text-ember-deep'>Asks when it doesn't.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={150} className='flex items-end lg:col-span-5'>
                        <p className='max-w-md text-lg leading-relaxed text-muted-foreground'>
                            Oriel remembers where things live and plans each job before it moves. When something is unclear, it checks with you first. Try a request.
                        </p>
                    </Reveal>
                </div>

                <Reveal className='mt-12 md:mt-16'>
                    <div className='grid gap-3 lg:grid-cols-12'>
                        <div className='flex flex-col gap-3 lg:col-span-4'>
                            <p id='planner-label' className='px-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground'>
                                Say to Oriel
                            </p>

                            <div role='group' aria-labelledby='planner-label' className='flex flex-col gap-2'>
                                {PLANNER_REQUESTS.map((item, index) => {
                                    const selected = index === active

                                    return (
                                        <button
                                            key={item.id}
                                            type='button'
                                            aria-pressed={selected}
                                            onClick={() => setActive(index)}
                                            className={cn(
                                                'group flex items-center justify-between gap-4 rounded-2xl border px-5 py-4 text-left text-[15px] transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                                                selected
                                                    ? 'border-foreground bg-foreground text-background'
                                                    : 'border-border bg-card text-foreground hover:border-foreground/40'
                                            )}
                                        >
                                            <span>
                                                &ldquo;{item.request}&rdquo;
                                            </span>

                                            <span
                                                className={cn(
                                                    'size-2 shrink-0 rounded-full transition-colors',
                                                    selected ? 'bg-ember' : 'bg-border group-hover:bg-foreground/30'
                                                )}
                                            />
                                        </button>
                                    )
                                })}
                            </div>

                            <p className='mt-auto hidden px-1 pt-4 text-sm leading-relaxed text-muted-foreground lg:block'>
                                A simplified view of what Oriel shows in the app while it works.
                            </p>
                        </div>

                        <div aria-live='polite' className='relative overflow-hidden rounded-[2rem] bg-ink p-6 text-ink-foreground md:p-10 lg:col-span-8'>
                            <div aria-hidden='true' className='pointer-events-none absolute -right-24 -top-24 size-80 rounded-full bg-ember/15 blur-[90px]' />

                            <div key={request.id} className='relative'>
                                <div className='flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-5'>
                                    <p className='font-heading text-3xl leading-tight md:text-4xl'>
                                        &ldquo;{request.request}&rdquo;
                                    </p>

                                    <span className='inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-foreground/70'>
                                        <span className='size-1.5 animate-pulse rounded-full bg-ember' />
                                        Planned · about {request.duration}
                                    </span>
                                </div>

                                <div className='mt-6 grid gap-8 md:grid-cols-5'>
                                    <div className='md:col-span-2'>
                                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                            What it knows
                                        </p>

                                        <ul className='mt-4 flex flex-col gap-3'>
                                            {request.context.map((line, index) => (
                                                <li
                                                    key={line}
                                                    style={{ animationDelay: `${index * 120}ms` }}
                                                    className='flex animate-rise items-start gap-3 text-[15px] leading-snug text-ink-foreground/80'
                                                >
                                                    <MapPin className='mt-0.5 size-4 shrink-0 text-ink-foreground/45' aria-hidden='true' />
                                                    {line}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className='md:col-span-3'>
                                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                            The plan
                                        </p>

                                        <ol className='mt-4 flex flex-col'>
                                            {request.steps.map((line, index) => (
                                                <li
                                                    key={line}
                                                    style={{ animationDelay: `${300 + index * 160}ms` }}
                                                    className='relative flex animate-rise gap-4 pb-4 last:pb-0'
                                                >
                                                    <span className='relative flex flex-col items-center'>
                                                        <span className='flex size-6 items-center justify-center rounded-full border border-white/20 font-mono text-[11px] text-ink-foreground/80'>
                                                            {index + 1}
                                                        </span>

                                                        {index < request.steps.length - 1 && <span className='mt-1 w-px flex-1 bg-white/15' />}
                                                    </span>

                                                    <span className='pt-0.5 text-[15px] leading-snug text-ink-foreground/85'>
                                                        {line}
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>

                                <div
                                    style={{ animationDelay: `${300 + request.steps.length * 160 + 150}ms` }}
                                    className='mt-8 flex animate-rise items-start gap-4 rounded-2xl border border-ember/30 bg-ember/10 p-5'
                                >
                                    <CircleHelp className='mt-0.5 size-5 shrink-0 text-ember' aria-hidden='true' />

                                    <div>
                                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                            Oriel checks first
                                        </p>

                                        <p className='mt-2 text-lg leading-snug text-ink-foreground'>
                                            &ldquo;{request.question}&rdquo;
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <div className='mt-3 grid gap-3 md:grid-cols-2'>
                    <Reveal>
                        <div className='h-full rounded-[2rem] border border-border bg-card p-7 md:p-10'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember-deep'>
                                What it does today
                            </p>

                            <ul className='mt-6 divide-y divide-border'>
                                {CAN_DO.map(item => (
                                    <li key={item} className='flex items-start gap-3 py-3.5 text-[15px] leading-snug text-foreground'>
                                        <Check className='mt-0.5 size-4 shrink-0 text-ember-deep' aria-hidden='true' />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>

                    <Reveal delay={120}>
                        <div className='flex h-full flex-col rounded-[2rem] bg-sand p-7 md:p-10'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground'>
                                What it doesn't do yet
                            </p>

                            <ul className='mt-6 divide-y divide-foreground/10'>
                                {NOT_YET.map(item => (
                                    <li key={item} className='flex items-start gap-3 py-3.5 text-[15px] leading-snug text-foreground/80'>
                                        <Minus className='mt-0.5 size-4 shrink-0 text-muted-foreground' aria-hidden='true' />
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            <p className='mt-auto pt-8 text-sm leading-relaxed text-muted-foreground'>
                                New skills arrive through monthly updates. We tell you what changed, and nothing new switches on without your say.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}

export default Intelligence
